import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  CircularProgress,
  Box,
  Avatar,
  Chip,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Skeleton,
  Alert,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { format, isToday, isYesterday } from "date-fns";
import urlconfig from "../config/urlconfig";
import { getApiToken } from "../authUtils";
import { useAuth } from "../AuthProvider";
import { useMsal } from "@azure/msal-react";
import { COLORS, FONT } from "../shared/DesignSystem";
import { describeLog } from "../shared/costSheetChangeLog";

// Saves log one row per changed field, each stamped separately — rows by the
// same person within this window are shown as a single edit.
const GROUP_WINDOW_MS = 5000;
const COLLAPSED_CHANGE_COUNT = 5;

const dayLabel = (d) => (isToday(d) ? "Today" : isYesterday(d) ? "Yesterday" : format(d, "EEEE, dd MMM yyyy"));

const initials = (name = "") =>
  name.split(/[\s@._]+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("") || "?";

const AVATAR_COLORS = ["#6366f1", "#0f766e", "#c2410c", "#6d28d9", "#0369a1", "#be123c", "#15803d", "#a16207"];
const avatarColor = (name = "") =>
  AVATAR_COLORS[[...name].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 0) % AVATAR_COLORS.length];

// Newest first; consecutive rows by the same person close together form one event.
// Each change log becomes readable rows; saves with nothing meaningful left are dropped.
const buildEvents = (logs, currentItems) => {
  const sorted = [...logs].sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt));
  const events = [];
  sorted.forEach((log) => {
    const at = new Date(log.changedAt);
    const isComment = log.fieldName === "Comment";
    const last = events[events.length - 1];
    if (
      last && !isComment && !last.isComment &&
      last.changedBy === (log.changedBy || "Unknown User") &&
      Math.abs(last.at - at) <= GROUP_WINDOW_MS
    ) {
      last.logs.push(log);
    } else {
      events.push({ at, changedBy: log.changedBy || "Unknown User", isComment, logs: [log] });
    }
  });
  return events
    .map((e) => {
      if (e.isComment) return { ...e, comment: e.logs[0].newValue };
      const rows = e.logs.flatMap((l) => describeLog(l, currentItems));
      const sections = [];
      rows.forEach((r) => {
        let sec = sections.find((x) => x.id === r.section.id);
        if (!sec) { sec = { ...r.section, rows: [] }; sections.push(sec); }
        // An "added"/"removed" marker labels the whole section; its values are the rows.
        if (r.note) sec.status = r.note;
        else sec.rows.push(r);
      });
      const order = { sheet: 0, item: 1, custom: 2 };
      sections.sort((x, y) => order[x.kind] - order[y.kind] || x.id.localeCompare(y.id, undefined, { numeric: true }));
      // An added or removed line/charge counts as one change, however many values it had.
      const changeCount = sections.reduce((n, sec) => n + (sec.status ? 1 : sec.rows.length), 0);
      return { ...e, sections, changeCount };
    })
    .filter((e) => e.isComment || e.changeCount > 0);
};

// "changed 3 things in Dell Laptop and cost sheet details"
const summarize = (sections, count) => {
  const names = sections.map((s) => s.subtitle || (s.kind === "sheet" ? "cost sheet details" : s.title));
  const where = names.length <= 2 ? names.join(" and ") : `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
  return `made ${count} change${count > 1 ? "s" : ""} to ${where}`;
};

const SECTION_ICON = {
  sheet: DescriptionOutlinedIcon,
  item: Inventory2OutlinedIcon,
  custom: ReceiptLongOutlinedIcon,
};

const ChangeRow = ({ row, status }) => {
  const { label, oldValue, newValue } = row;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "minmax(150px, 190px) 1fr" },
        gap: { xs: 0.5, sm: 1.5 },
        alignItems: "center",
        py: 0.9,
        "&:not(:last-of-type)": { borderBottom: `1px dashed ${COLORS.border}` },
      }}
    >
      <Typography variant="body2" sx={{ color: COLORS.textMuted, fontWeight: 600 }}>{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
        {status === "added" ? (
          <ValuePill value={newValue} tone="new" />
        ) : status === "removed" ? (
          <ValuePill value={oldValue} tone="old" />
        ) : oldValue == null ? (
          <>
            <Typography variant="caption" sx={{ color: COLORS.textFaint, fontWeight: 600 }}>set to</Typography>
            <ValuePill value={newValue} tone="new" />
          </>
        ) : newValue == null ? (
          <>
            <ValuePill value={oldValue} tone="old" />
            <Typography variant="caption" sx={{ color: COLORS.danger, fontWeight: 600 }}>cleared</Typography>
          </>
        ) : (
          <>
            <ValuePill value={oldValue} tone="old" />
            <ArrowForwardIcon sx={{ fontSize: 16, color: COLORS.textFaint }} />
            <ValuePill value={newValue} tone="new" />
          </>
        )}
      </Box>
    </Box>
  );
};

const ChangeSection = ({ section }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = SECTION_ICON[section.kind];
  const { status } = section;
  // Everything in an added/removed line is shown — the point is to see exactly what it held.
  const visible = expanded || status ? section.rows : section.rows.slice(0, COLLAPSED_CHANGE_COUNT);
  const hidden = section.rows.length - visible.length;
  return (
    <Box sx={{ "&:not(:first-of-type)": { borderTop: `1px solid ${COLORS.border}` } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.75, py: 1, bgcolor: COLORS.surfaceAlt }}>
        <Icon sx={{ fontSize: 18, color: section.kind === "item" ? COLORS.indigo : COLORS.textMuted }} />
        <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.textPrimary }}>{section.title}</Typography>
        {section.subtitle && (
          <Typography variant="body2" sx={{ color: COLORS.textMuted, minWidth: 0 }} noWrap title={section.subtitle}>
            · {section.subtitle}
          </Typography>
        )}
        {status && (
          <Chip size="small" label={status === "added" ? "Added" : "Removed"}
            sx={{ ml: "auto", height: 22, fontWeight: 700,
                  bgcolor: status === "added" ? COLORS.successBg : COLORS.dangerBg,
                  color: status === "added" ? "#047857" : "#b91c1c" }} />
        )}
      </Box>
      <Box sx={{ px: 1.75 }}>
        {visible.map((r, i) => <ChangeRow key={i} row={r} status={status} />)}
        {status && section.rows.length === 0 && (
          <Typography variant="body2" sx={{ py: 1, color: COLORS.textFaint, fontStyle: "italic" }}>
            Its details weren't recorded with this entry.
          </Typography>
        )}
        {!status && section.rows.length > COLLAPSED_CHANGE_COUNT && (
          <Button size="small" onClick={() => setExpanded((e) => !e)}
            sx={{ textTransform: "none", fontWeight: 600, color: COLORS.indigo, px: 0, my: 0.5 }}>
            {expanded ? "Show less" : `Show ${hidden} more`}
          </Button>
        )}
      </Box>
    </Box>
  );
};

const ValuePill = ({ value, tone }) => (
  <Box
    component="span"
    sx={{
      px: 1, py: 0.25, borderRadius: 1, fontSize: 13, fontFamily: FONT.mono,
      wordBreak: "break-word", maxWidth: "100%",
      ...(tone === "old"
        ? { bgcolor: COLORS.dangerBg, color: "#b91c1c", textDecoration: "line-through", textDecorationColor: "rgba(185,28,28,0.5)" }
        : { bgcolor: COLORS.successBg, color: "#047857", fontWeight: 600 }),
    }}
  >
    {value}
  </Box>
);

const EventCard = ({ event }) => {
  const { isComment, changedBy, at, sections, changeCount, comment } = event;
  return (
    <Box sx={{ display: "flex", gap: 1.5, position: "relative", pb: 2.5 }}>
      {/* timeline rail */}
      <Box sx={{ position: "absolute", left: 17, top: 40, bottom: 0, width: 2, bgcolor: COLORS.border }} />
      <Avatar sx={{ width: 36, height: 36, fontSize: 13, fontWeight: 700, bgcolor: avatarColor(changedBy), zIndex: 1 }}>
        {initials(changedBy)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap", mb: 0.75 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.textPrimary }}>{changedBy}</Typography>
          <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
            {isComment ? "added a comment" : summarize(sections, changeCount)}
          </Typography>
          <Tooltip title={format(at, "dd MMM yyyy, hh:mm:ss a")} arrow>
            <Typography variant="caption" sx={{ color: COLORS.textFaint, ml: "auto", cursor: "default" }}>
              {format(at, "hh:mm a")}
            </Typography>
          </Tooltip>
        </Box>

        {isComment ? (
          <Box
            sx={{
              bgcolor: COLORS.indigoBg, border: "1px solid #e0e7ff", borderRadius: "4px 12px 12px 12px",
              px: 1.75, py: 1.25, color: COLORS.textPrimary, whiteSpace: "pre-wrap", wordBreak: "break-word",
            }}
          >
            <Typography variant="body2">{comment}</Typography>
          </Box>
        ) : (
          <Box sx={{ bgcolor: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 2, overflow: "hidden" }}>
            {sections.map((sec) => <ChangeSection key={sec.id} section={sec} />)}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const CostSheetHistory = ({ selectedItem }) => {
  const { username } = useAuth();
  const { instance, accounts } = useMsal();
  const account = accounts?.[0] || null;

  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [commentError, setCommentError] = useState(null);
  const [filter, setFilter] = useState("all");

  const sendAuthenticatedRequest = async (url, method, body = null) => {
    try {
      const accessToken = await getApiToken(instance, account);
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: body ? JSON.stringify(body) : null,
      });

      if (!response.ok) {
        const text = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { message: text || "Unknown error" };
        }
        throw new Error(`${response.status}: ${errorData.message}`);
      }

      const contentType = response.headers.get("content-type");
      return contentType?.includes("application/json")
        ? response.json()
        : response.text();
    } catch (error) {
      console.error("Authenticated API call failed:", error);
      throw error;
    }
  };

  const fetchLogs = async ({ showLoader = true } = {}) => {
    if (!selectedItem?.id) return;
    if (showLoader) setLoadingLogs(true);
    setLoadError(null);
    try {
      const data = await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/logs/get-item/${selectedItem.id}`,
        "GET"
      );
      setLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching logs:", error);
      setLoadError("Couldn't load the history for this cost sheet.");
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleOpen = () => {
    if (!selectedItem?.id) return;
    setOpen(true);
    setFilter("all");
    setCommentError(null);
    fetchLogs();
  };

  const handleClose = () => setOpen(false);

  const handleSubmitComment = async () => {
    if (!comment.trim() || submitting) return;
    setSubmitting(true);
    setCommentError(null);
    try {
      await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/logs/item/${selectedItem.id}/comment`,
        "POST",
        {
          comment,
          changedBy: username,
        }
      );
      setComment("");
      await fetchLogs({ showLoader: false }); // refresh logs to include the new comment
    } catch (err) {
      console.error("Error adding comment:", err);
      setCommentError("Failed to add comment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const allEvents = useMemo(() => buildEvents(logs, selectedItem?.items || []), [logs, selectedItem]);
  const counts = useMemo(() => ({
    all: allEvents.length,
    changes: allEvents.filter((e) => !e.isComment).length,
    comments: allEvents.filter((e) => e.isComment).length,
  }), [allEvents]);

  const dayGroups = useMemo(() => {
    const events = allEvents.filter((e) =>
      filter === "all" || (filter === "comments" ? e.isComment : !e.isComment));
    const groups = [];
    events.forEach((e) => {
      const label = dayLabel(e.at);
      const last = groups[groups.length - 1];
      if (last?.label === label) last.events.push(e);
      else groups.push({ label, events: [e] });
    });
    return groups;
  }, [allEvents, filter]);

  const lastUpdated = selectedItem?.updatedAT ? format(new Date(selectedItem.updatedAT), "dd MMM yyyy, hh:mm a") : "N/A";

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        startIcon={<HistoryIcon />}
        onClick={handleOpen}
        disabled={!selectedItem}
      >
        View Cost Sheet History
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        style={{ zIndex: 1300 }}
        PaperProps={{ sx: { borderRadius: 3, height: "min(88vh, 860px)", display: "flex", flexDirection: "column", overflow: "hidden" } }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3, py: 2.25, color: COLORS.white, fontFamily: FONT.display,
            background: `linear-gradient(135deg, ${COLORS.navy} 0%, ${COLORS.navyLight} 100%)`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: "rgba(255,255,255,0.12)", display: "grid", placeItems: "center" }}>
              <HistoryIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: FONT.display, lineHeight: 1.2 }}>
                Cost Sheet History
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.75 }} noWrap>
                {selectedItem?.salesOrderNo || "N/A"}{selectedItem?.clientName ? ` · ${selectedItem.clientName}` : ""}
              </Typography>
            </Box>
            <IconButton onClick={handleClose} sx={{ color: COLORS.white }} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
            {[
              ["Created by", selectedItem?.createdBy || "N/A"],
              ["Last updated", lastUpdated],
            ].map(([k, v]) => (
              <Chip key={k} size="small" label={<><span style={{ opacity: 0.7 }}>{k}:</span> <strong>{v}</strong></>}
                sx={{ bgcolor: "rgba(255,255,255,0.12)", color: COLORS.white, height: 26 }} />
            ))}
          </Box>
        </Box>

        {/* Toolbar */}
        <Box sx={{ px: 3, py: 1.5, display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", borderBottom: `1px solid ${COLORS.border}`, bgcolor: COLORS.white }}>
          <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 1.5 },
                  "& .Mui-selected": { bgcolor: `${COLORS.indigoBg} !important`, color: `${COLORS.indigo} !important` } }}>
            <ToggleButton value="all">All ({counts.all})</ToggleButton>
            <ToggleButton value="changes"><EditNoteIcon sx={{ fontSize: 18, mr: 0.5 }} />Changes ({counts.changes})</ToggleButton>
            <ToggleButton value="comments"><ChatBubbleOutlineIcon sx={{ fontSize: 16, mr: 0.5 }} />Comments ({counts.comments})</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Timeline */}
        <DialogContent sx={{ flex: 1, bgcolor: COLORS.surface, px: 3, py: 2 }}>
          {!selectedItem ? (
            <Typography>No cost sheet selected.</Typography>
          ) : loadingLogs ? (
            [0, 1, 2].map((i) => (
              <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 3 }}>
                <Skeleton variant="circular" width={36} height={36} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="40%" />
                  <Skeleton variant="rounded" height={64} sx={{ mt: 1 }} />
                </Box>
              </Box>
            ))
          ) : loadError ? (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => fetchLogs()}>Retry</Button>}>
              {loadError}
            </Alert>
          ) : dayGroups.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8, color: COLORS.textMuted }}>
              <HistoryIcon sx={{ fontSize: 48, color: COLORS.textFaint, mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.textPrimary }}>
                {filter === "comments" ? "No comments yet" : filter === "changes" ? "No changes yet" : "No history yet"}
              </Typography>
              <Typography variant="body2">
                {filter === "comments"
                  ? "Comments added below will appear here."
                  : "Edits and comments on this cost sheet will appear here."}
              </Typography>
            </Box>
          ) : (
            dayGroups.map((group) => (
              <Box key={group.label} sx={{ mb: 1 }}>
                <Typography variant="overline"
                  sx={{ display: "block", fontWeight: 800, color: COLORS.textMuted, letterSpacing: "0.08em", mb: 1.5,
                        position: "sticky", top: -16, bgcolor: COLORS.surface, zIndex: 2, py: 0.5 }}>
                  {group.label}
                </Typography>
                {group.events.map((e, i) => (
                  <EventCard key={`${e.at.getTime()}-${e.changedBy}-${i}`} event={e} />
                ))}
              </Box>
            ))
          )}
        </DialogContent>

        {/* Comment composer */}
        <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${COLORS.border}`, bgcolor: COLORS.white }}>
          {commentError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setCommentError(null)}>{commentError}</Alert>}
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-end" }}>
            <Avatar sx={{ width: 36, height: 36, fontSize: 13, fontWeight: 700, bgcolor: avatarColor(username || ""), display: { xs: "none", sm: "flex" } }}>
              {initials(username || "")}
            </Avatar>
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={6}
              size="small"
              placeholder="Add a comment..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleSubmitComment(); }
              }}
              disabled={!selectedItem || submitting}
              helperText="Ctrl / ⌘ + Enter to send"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: COLORS.surface } }}
            />
            <Button
              variant="contained"
              onClick={handleSubmitComment}
              disabled={!comment.trim() || submitting || !selectedItem}
              endIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              sx={{ mb: 2.75, borderRadius: 2, textTransform: "none", fontWeight: 700, bgcolor: COLORS.indigo,
                    "&:hover": { bgcolor: COLORS.navy }, whiteSpace: "nowrap" }}
            >
              Comment
            </Button>
          </Box>
        </Box>
      </Dialog>
    </div>
  );
};

export default CostSheetHistory;
